<template>
  <div class="header">
    <div class="title-search">
      <!-- Sadece masaüstünde görünecek başlık -->
      <h2 class="desktop-title">
        <img src="@/assets/logo.png" alt="App Icon" class="title-icon" />
        Temel Analiz Raporu
      </h2>
      <div class="search-container">
        <!-- Mobil ekranlarda görünecek logo -->
        <img src="@/assets/logo.png" alt="App Icon" class="mobile-logo" />

        <!-- Hisseler boş olduğunda loading animasyon göster -->
        <div
          v-if="!hisseler || hisseler.length === 0"
          class="loading-container"
        >
          <div class="loading-text">Hisseler yükleniyor</div>
          <div class="loading-animation">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="loading-icon"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 6v6l4 2"></path>
            </svg>
          </div>
        </div>

        <!-- Hisseler yüklendiğinde arama kutusu göster -->
        <template v-else>
          <input
            type="text"
            placeholder="Hisse ara..."
            v-model="searchQuery"
            @focus="showDropdown = true"
            @input="showDropdown = true"
            id="hisse-search"
            name="hisse-search"
          />
          <button
            class="clear-button"
            @click="clearSearchQuery"
            v-if="searchQuery.length > 0"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </button>

          <!-- Toggle button for dropdown - always visible -->
          <button class="close-button" @click="toggleDropdown">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M6 9l6 6 6-6" v-if="showDropdown" />
              <path d="M6 15l6-6 6 6" v-else />
            </svg>
          </button>

          <!-- Dropdown for filtered stocks -->
          <div
            v-if="showDropdown && filteredHisseler.length > 0"
            class="hisse-dropdown"
          >
            <div
              v-for="hisse in filteredHisseler"
              :key="hisse.hisse_kodu"
              @click="selectHisseAndCloseDropdown(hisse)"
              class="hisse-card"
              :class="{
                active:
                  selectedHisse &&
                  selectedHisse.hisse_kodu === hisse.hisse_kodu,
              }"
            >
              <div class="hisse-kod">{{ hisse.hisse_kodu }}</div>
              <div class="hisse-fiyat">{{ formatPrice(hisse.son_fiyat) }}</div>
              <div
                class="hisse-degisim"
                :class="{
                  positive: hisse.degisim_yuzde > 0,
                  negative: hisse.degisim_yuzde < 0,
                  neutral: hisse.degisim_yuzde === 0,
                }"
              >
                <span class="degisim-icon">
                  <svg
                    v-if="hisse.degisim_yuzde > 0"
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="18 15 12 9 6 15" />
                  </svg>
                  <svg
                    v-else-if="hisse.degisim_yuzde < 0"
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </span>
                {{ formatPrice(hisse.degisim_yuzde) }}%
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { formatPrice } from "@/utils/formatHelpers";

export default {
  props: {
    hisseler: {
      type: Array,
      required: true,
    },
    selectedHisse: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      searchQuery: "",
      showDropdown: false,
    };
  },
  computed: {
    filteredHisseler() {
      if (!this.searchQuery) return this.hisseler;

      const normalizedQuery = this.normalizeText(this.searchQuery);

      return this.hisseler.filter((hisse) => {
        const normalizedHisseKodu = this.normalizeText(hisse.hisse_kodu);
        return normalizedHisseKodu.includes(normalizedQuery);
      });
    },
  },
  watch: {
    hisseler: {
      immediate: true,
      handler(newHisseler) {
        if (newHisseler && newHisseler.length > 0) {
          this.showDropdown = true;
        }
      },
    },
  },
  methods: {
    normalizeText(text) {
      // Türkçe karakterleri normalize et
      return text
        .toLowerCase()
        .replace(/ı/g, "i")
        .replace(/i̇/g, "i") // noktalı büyük İ
        .replace(/ö/g, "o")
        .replace(/ü/g, "u")
        .replace(/ş/g, "s")
        .replace(/ç/g, "c")
        .replace(/ğ/g, "g");
    },
    selectHisseAndCloseDropdown(hisse) {
      this.$emit("select-hisse", hisse);
      this.searchQuery = hisse.hisse_kodu;
      this.showDropdown = false;
    },
    clearSearchQuery() {
      this.searchQuery = "";
      this.showDropdown = false;
    },
    formatPrice(value) {
      return formatPrice(value);
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;

      // Close/open tuşuna basıldığında input temizlensin
      if (this.showDropdown) {
        this.searchQuery = "";
      }
    },
  },
};
</script>

<style scoped>
.header {
  background-color: var(--card-bg);
  border-bottom: 1px solid var(--border-color);
  padding: 1rem;
}

.title-search {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.title-search h2 {
  color: var(--primary-light);
  font-size: 1.5rem;
  white-space: nowrap;
  margin-right: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.title-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.search-container {
  position: relative;
  flex: 1;
  max-width: 400px;
  display: flex;
  align-items: center;
}

/* Mobil logo - varsayılan olarak gizli */
.mobile-logo {
  width: 36px;
  height: 36px;
  object-fit: contain;
  margin-right: 10px;
  display: none;
}

.search-container input {
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background-color: var(--card-dark);
  color: var(--text-primary);
  font-size: 14px;
  outline: none;
  transition: all 0.2s ease;
}

.search-container input:focus {
  border-color: var(--primary-light);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
}

.search-container input::placeholder {
  color: var(--text-muted);
}

/* Yükleme Animasyonu Stilleri */
.loading-container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--card-dark);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  padding: 0.75rem 1rem;
}

.loading-text {
  color: var(--text-secondary);
  font-size: 14px;
  text-align: left;
}

.loading-animation {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.loading-icon {
  color: var(--primary-light);
  animation: spin 1.5s linear infinite;
  width: 16px;
  height: 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.clear-button,
.close-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.clear-button {
  right: 40px;
}

.close-button {
  right: 10px;
}

.clear-button:hover,
.close-button:hover {
  color: var(--primary-light);
  background-color: rgba(59, 130, 246, 0.1);
}

.clear-button svg,
.close-button svg {
  width: 16px;
  height: 16px;
}

.hisse-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 500px;
  overflow-y: auto;
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  margin-top: 5px;
  z-index: 10;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.hisse-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem; /* Azaltılmış padding */
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid var(--border-color);
  font-size: 13px; /* Yazı boyutu küçültüldü */
  min-height: 36px; /* Minimum yükseklik belirlendi */
}

.hisse-card:last-child {
  border-bottom: none;
}

.hisse-card:hover {
  background-color: var(--hover-bg);
}

.hisse-card.active {
  background-color: rgba(59, 130, 246, 0.1);
}

.hisse-kod {
  font-weight: 600;
  font-size: 13px; /* Küçültülmüş font */
}

.hisse-fiyat {
  font-size: 13px; /* Küçültülmüş font */
  color: var(--text-secondary);
}

.hisse-degisim {
  display: flex;
  align-items: center;
  gap: 4px; /* Azaltılmış gap */
  font-size: 13px; /* Küçültülmüş font */
  font-weight: 500;
  padding: 2px 6px; /* Azaltılmış padding */
  border-radius: 4px; /* Azaltılmış border-radius */
  background: var(--tag-bg);
}

.degisim-icon {
  display: flex;
  align-items: center;
  padding: 1px; /* Azaltılmış padding */
}

.positive {
  color: var(--success-color);
  --tag-bg: rgba(34, 197, 94, 0.1);
}

.negative {
  color: var(--danger-color);
  --tag-bg: rgba(239, 68, 68, 0.1);
}

.neutral {
  color: var(--text-secondary);
  --tag-bg: rgba(156, 163, 175, 0.1);
}

/* İkon hover efektleri */
.hisse-card:hover .degisim-icon svg {
  transform: scale(1.1);
  transition: transform 0.2s ease;
}

@media (max-width: 768px) {
  .title-search {
    flex-direction: column;
    align-items: flex-start;
  }

  .desktop-title {
    display: none !important;
  }

  .search-container {
    width: 100%;
    max-width: 100%;
  }

  .mobile-logo {
    display: block;
  }
}
</style>
